'use client';

import './styles/@theme';
import { useWalletUm } from '@/hooks/use-wallet-um';
import { getText } from '@/utils/text/text';
import { formatNumberDefault } from '@/utils/numbers/format-number';
import { SvgLoader } from '@/app/elements/svg/loader';
import { useNotIdle } from '@/hooks/use-not-idle';
import { type RootState, useStore } from '@/store/store';
import { useEffect, useRef } from 'react';
import { MessageBroker } from '@/utils/message-broker';
import { MESSAGES, type Message } from '@/utils/message-broker/types';
import { useGlobalContext } from '@/store/contexts/global-context';

const DOCUMENT_EVENTS: (keyof HTMLElementEventMap)[] = ['click', 'keydown', 'scroll', 'touchstart'];

export const Balance: React.FC = () => {
    const { balance, getWalletBalance, triggerDeposit } = useWalletUm();
    const { isAndroidWrapper } = useGlobalContext();
    const showBalance = useStore((state: RootState) => state.userContext.accountSettings.showBalance);
    const initSessionCompleted = useStore((state: RootState) => state.userContext.session.initCompleted);

    const getWalletBalanceRef = useRef(getWalletBalance);

    useNotIdle(getWalletBalance, {
        events: DOCUMENT_EVENTS
    });

    useEffect(() => {
        getWalletBalanceRef.current = getWalletBalance;
    }, [getWalletBalance]);

    useEffect(() => {
        if (initSessionCompleted) {
            getWalletBalance();
        }
    }, [initSessionCompleted]);

    useEffect(() => {
        if (!isAndroidWrapper) return;

        const handleModularDepositDismissed = (message: Message) => {
            if (message.data?.type !== MESSAGES.MODULAR_DEPOSIT_DISMISSED) return;
            getWalletBalanceRef.current();
        };

        MessageBroker.subscribe(handleModularDepositDismissed);

        return () => MessageBroker.unsubscribe(handleModularDepositDismissed);
    }, [isAndroidWrapper]);

    const displayBalance = balance
        ? showBalance
            ? formatNumberDefault(balance.playableBalance)
            : '* * * * * *'
        : getText({ text: 'components.balance.loading' });

    const handleDeposit = () => {
        if (!initSessionCompleted) {
            console.log('Session not initialized yet. Deposit action aborted.');
            return;
        }
        triggerDeposit();
    };

    return (
        <div className="balance-container">
            <div className="funds">
                <p className="value">{displayBalance}</p>
                <p className="text">{getText({ text: 'components.balance.addFunds' })}</p>
            </div>
            <button
                onClick={handleDeposit}
                disabled={!initSessionCompleted}
                className={`btn add-funds${!initSessionCompleted ? ' disabled' : ''}`}>
                <SvgLoader iconName="plus" className="add-funds-icon" />
            </button>
        </div>
    );
};
